// src/ids.ts
import { z } from "zod";
var uuid = z.string().uuid();
var tenantId = uuid.describe("Tenant (agencia/MSP). Ra\xEDz de todo el aislamiento.");
var clientId = uuid.describe("Cliente del tenant.");
var branchId = uuid.describe("Sucursal de un cliente.");
var nodeId = uuid.describe("Nodo (m\xE1quina con agente instalado).");
var userId = uuid.describe("Operador del OCC.");
var sessionId = uuid.describe("Sesi\xF3n de soporte (pantalla o terminal).");
var pluginId = uuid.describe("Plugin (biblioteca de scripts parametrizados, M6.1).");
var nodeAlias = z.string().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, "Solo letras, n\xFAmeros, punto, guion y guion bajo");
var nodeRef = z.union([
  z.object({ nodeId }),
  z.object({ alias: nodeAlias, branchId: branchId.optional() })
]);

// src/scopes.ts
import { z as z2 } from "zod";
var scopeType = z2.enum(["tenant", "client", "branch", "node"]);
var SCOPE_BREADTH = {
  tenant: 3,
  client: 2,
  branch: 1,
  node: 0
};
var role = z2.enum([
  /** Administra el tenant completo: jerarquía, usuarios, permisos, branding. */
  "tenant_admin",
  /** Opera nodos dentro de su alcance: pantalla, terminal, scripts, archivos. */
  "support_technician",
  /** Ve inventario, salud y bitácora dentro de su alcance. Sin acciones. */
  "read_only"
]);
var permission = z2.object({
  scopeType,
  scopeId: uuid,
  role
});
var actor = z2.object({
  userId: uuid,
  tenantId: uuid,
  permissions: z2.array(permission),
  /** Canal de origen; solo se usa para auditar, nunca para autorizar. */
  channel: z2.enum(["panel", "mcp", "api", "system"])
});
var resourcePath = z2.object({
  tenantId: uuid,
  clientId: uuid.optional(),
  branchId: uuid.optional(),
  nodeId: uuid.optional()
});

// src/node.ts
import { z as z3 } from "zod";
var osFamily = z3.enum(["windows", "linux", "darwin"]);
var nodeStatus = z3.enum(["online", "offline", "unknown"]);
var nodeType = z3.enum(["vps", "fisico"]);
var nodeInventory = z3.object({
  hostname: z3.string(),
  osFamily,
  osVersion: z3.string(),
  architecture: z3.string(),
  cpuModel: z3.string().optional(),
  cpuCores: z3.number().int().positive().optional(),
  memoryTotalBytes: z3.number().int().nonnegative().optional(),
  disks: z3.array(
    z3.object({
      mountpoint: z3.string(),
      totalBytes: z3.number().int().nonnegative(),
      freeBytes: z3.number().int().nonnegative()
    })
  ).default([]),
  localIps: z3.array(z3.string()).default([]),
  installedSoftware: z3.array(z3.object({ name: z3.string(), version: z3.string().optional() })).default([]),
  agentVersion: z3.string(),
  collectedAt: z3.coerce.date()
});
var healthSample = z3.object({
  cpuPercent: z3.number().min(0).max(100).optional(),
  memoryUsedPercent: z3.number().min(0).max(100).optional(),
  diskUsedPercent: z3.number().min(0).max(100).optional(),
  uptimeSeconds: z3.number().int().nonnegative().optional(),
  sampledAt: z3.coerce.date()
});
var nodeSummary = z3.object({
  id: nodeId,
  tenantId,
  branchId,
  alias: nodeAlias,
  status: nodeStatus,
  lastSeenAt: z3.coerce.date().nullable(),
  osFamily,
  agentVersion: z3.string().nullable(),
  /** La reporta el agente en su inventario; no se edita a mano. */
  ip: z3.string().nullable(),
  provider: z3.string().nullable(),
  monthlyCostCents: z3.number().int().nonnegative().nullable(),
  nodeType: nodeType.nullable(),
  /** Promedio ponderado de las últimas 24 h a resolución `1h`. `null` sin muestras aún. */
  cpuAvgPercent: z3.number().min(0).max(100).nullable(),
  memAvgPercent: z3.number().min(0).max(100).nullable()
});

// src/actions.ts
import { z as z8 } from "zod";

// src/alerts.ts
import { z as z4 } from "zod";
var alertMetric = z4.enum([
  "node_offline",
  "cpu_percent",
  "memory_percent",
  "disk_percent",
  "service_down",
  "process_down",
  "container_down"
]);
var METRIC_UNIT = {
  node_offline: "seconds",
  cpu_percent: "percent",
  memory_percent: "percent",
  disk_percent: "percent",
  service_down: "boolean",
  process_down: "boolean",
  container_down: "boolean"
};
var NAMED_TARGET_METRICS = ["service_down", "process_down", "container_down"];
var NODE_WATCH_METRICS = ["process_down", "container_down"];
function isNamedTargetMetric(metric) {
  return NAMED_TARGET_METRICS.includes(metric);
}
function isNodeWatchMetric(metric) {
  return NODE_WATCH_METRICS.includes(metric);
}
var alertComparison = z4.enum(["above", "below"]);
var alertSeverity = z4.enum(["info", "warning", "critical"]);
var alertRuleSource = z4.enum(["manual", "node_toggle", "node_watch"]);
var SEVERITY_RANK = {
  info: 0,
  warning: 1,
  critical: 2
};
var alertEvalState = z4.enum(["ok", "pending", "firing", "clearing"]);
var alertEffect = z4.enum(["none", "fired", "renotified", "resolved"]);
var alertRuleName = z4.string().min(1).max(120);
var alertRuleSpec = z4.object({
  name: alertRuleName,
  description: z4.string().max(500).optional(),
  enabled: z4.boolean().default(true),
  severity: alertSeverity.default("warning"),
  metric: alertMetric,
  comparison: alertComparison.default("above"),
  /**
   * Ámbito de aplicación dentro del tenant. `tenant` cubre todos los nodos;
   * `client` y `branch`, los que cuelgan de ese nivel; `node`, uno solo. Es el
   * mismo vocabulario del RBAC porque es la misma jerarquía: así una regla
   * "disco > 90 % en el cliente Frutal" no necesita enumerar nodos.
   */
  scopeType: scopeType.default("tenant"),
  /** Vacío cuando `scopeType` es `tenant`: el ámbito es el tenant del actor. */
  scopeId: uuid.optional(),
  /**
   * Nombre de lo vigilado: el servicio de `service_down`, el proceso de
   * `process_down` o el contenedor de `container_down`. Obligatorio en esos tres
   * casos y prohibido en el resto: una regla sin nombre se dispararía con el
   * primer servicio que cambie en cualquier nodo.
   */
  service: z4.string().min(1).max(120).optional(),
  /** Punto de montaje para `disk_percent`. Sin él, se toma el disco más lleno. */
  mountpoint: z4.string().min(1).max(255).optional(),
  /** Umbral de encendido. Porcentaje, segundos o 1/0 según la métrica. */
  threshold: z4.number().finite(),
  /**
   * Umbral de apagado. Por debajo (o por encima, en `below`) de este valor la
   * alerta se considera resuelta. Igualarlo a `threshold` desactiva la banda
   * muerta y devuelve el comportamiento sin histéresis.
   */
  clearThreshold: z4.number().finite(),
  /** Segundos que la condición debe sostenerse antes de encender. */
  forSeconds: z4.number().int().nonnegative().max(86400).default(120),
  /** Segundos que debe sostenerse la recuperación antes de apagar. */
  clearForSeconds: z4.number().int().nonnegative().max(86400).default(120),
  /**
   * Cada cuánto se repite el aviso de una alerta que sigue encendida. `0`
   * significa avisar una sola vez. Repetir importa: una alerta crítica que
   * avisó a las 3 de la mañana y nadie vio tiene que volver a hablar.
   */
  renotifySeconds: z4.number().int().nonnegative().max(604800).default(0),
  /**
   * Ventana de agrupación. Los avisos de esta regla que caigan dentro de la
   * ventana salen en un solo mensaje. `0` desactiva la agrupación.
   */
  groupWindowSeconds: z4.number().int().nonnegative().max(3600).default(60),
  /** Canales por los que sale el aviso. Vacío = regla que solo deja alerta en el panel. */
  channelIds: z4.array(uuid).max(20).default([])
}).superRefine((rule, ctx) => {
  if (rule.comparison === "above" && rule.clearThreshold > rule.threshold) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["clearThreshold"],
      message: 'En una regla "above" el umbral de apagado no puede ser mayor que el de encendido: la alerta se apagar\xEDa al instante'
    });
  }
  if (rule.comparison === "below" && rule.clearThreshold < rule.threshold) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["clearThreshold"],
      message: 'En una regla "below" el umbral de apagado no puede ser menor que el de encendido: la alerta se apagar\xEDa al instante'
    });
  }
  if (isNamedTargetMetric(rule.metric) && !rule.service) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["service"],
      message: `Una regla ${rule.metric} tiene que decir qu\xE9 servicio, proceso o contenedor vigila`
    });
  }
  if (!isNamedTargetMetric(rule.metric) && rule.service) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["service"],
      message: `La m\xE9trica ${rule.metric} no vigila un servicio, proceso o contenedor concreto`
    });
  }
  if (isNodeWatchMetric(rule.metric) && rule.scopeType !== "node") {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["scopeType"],
      message: `Una regla ${rule.metric} solo aplica a un nodo concreto`
    });
  }
  if (rule.scopeType !== "tenant" && !rule.scopeId) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["scopeId"],
      message: `Una regla de \xE1mbito ${rule.scopeType} tiene que decir sobre qu\xE9 ${rule.scopeType} aplica`
    });
  }
});
var updateAlertRuleRequest = z4.object({
  name: alertRuleName.optional(),
  description: z4.string().max(500).nullable().optional(),
  enabled: z4.boolean().optional(),
  severity: alertSeverity.optional(),
  threshold: z4.number().finite().optional(),
  clearThreshold: z4.number().finite().optional(),
  forSeconds: z4.number().int().nonnegative().max(86400).optional(),
  clearForSeconds: z4.number().int().nonnegative().max(86400).optional(),
  renotifySeconds: z4.number().int().nonnegative().max(604800).optional(),
  groupWindowSeconds: z4.number().int().nonnegative().max(3600).optional(),
  channelIds: z4.array(uuid).max(20).optional(),
  mountpoint: z4.string().min(1).max(255).nullable().optional()
});
var alertRuleView = z4.object({
  id: uuid,
  tenantId,
  name: alertRuleName,
  description: z4.string().nullable(),
  enabled: z4.boolean(),
  severity: alertSeverity,
  metric: alertMetric,
  comparison: alertComparison,
  scopeType,
  scopeId: uuid.nullable(),
  service: z4.string().nullable(),
  mountpoint: z4.string().nullable(),
  threshold: z4.number(),
  clearThreshold: z4.number(),
  forSeconds: z4.number().int(),
  clearForSeconds: z4.number().int(),
  renotifySeconds: z4.number().int(),
  groupWindowSeconds: z4.number().int(),
  channelIds: z4.array(uuid),
  source: alertRuleSource,
  createdAt: z4.coerce.date(),
  updatedAt: z4.coerce.date()
});
var nodeToggleMetric = z4.enum(["node_offline", "cpu_percent", "memory_percent"]);
var nodeToggleRequest = z4.object({
  enabled: z4.boolean(),
  /** Vacío es válido: un interruptor encendido sin canales solo deja alerta en el panel. */
  channelIds: z4.array(uuid).max(10).default([])
});
var nodeToggleView = z4.object({
  enabled: z4.boolean(),
  channelIds: z4.array(uuid),
  /** `null` cuando nunca se activó: no hay regla que borrar ni que mostrar. */
  ruleId: uuid.nullable()
});
var nodeWatchKind = z4.enum(["process", "container"]);
var WATCH_METRIC = {
  process: "process_down",
  container: "container_down"
};
var NODE_WATCH_DOWN_SECONDS = 300;
var nodeWatchTarget = z4.string().trim().min(1).max(60).refine((value) => !value.slice(0, -1).includes("*") && value !== "*", {
  message: "El comod\xEDn * solo puede ir al final y acompa\xF1ando un prefijo"
});
var nodeWatchRequest = z4.object({
  kind: nodeWatchKind,
  target: nodeWatchTarget,
  enabled: z4.boolean().default(true),
  channelIds: z4.array(uuid).max(10).default([])
});
var nodeWatchStatus = z4.enum(["unknown", "ok", "pending", "down"]);
var nodeWatchView = z4.object({
  ruleId: uuid,
  kind: nodeWatchKind,
  target: z4.string(),
  enabled: z4.boolean(),
  channelIds: z4.array(uuid),
  /** Segundos que debe llevar caído antes de avisar. */
  forSeconds: z4.number().int(),
  status: nodeWatchStatus,
  /** Desde cuándo está en ese estado, si se sabe. */
  since: z4.coerce.date().nullable()
});
var alertStatus = z4.enum(["firing", "resolved"]);
var alertView = z4.object({
  id: uuid,
  tenantId,
  ruleId: uuid,
  ruleName: alertRuleName,
  nodeId,
  nodeAlias: z4.string().nullable(),
  clientId: clientId.nullable(),
  branchId: branchId.nullable(),
  metric: alertMetric,
  severity: alertSeverity,
  status: alertStatus,
  value: z4.number().nullable(),
  threshold: z4.number(),
  firstBreachAt: z4.coerce.date(),
  openedAt: z4.coerce.date(),
  resolvedAt: z4.coerce.date().nullable(),
  lastNotifiedAt: z4.coerce.date().nullable(),
  notifyCount: z4.number().int().nonnegative(),
  acknowledgedAt: z4.coerce.date().nullable(),
  acknowledgedByUserId: userId.nullable(),
  /** Nota opcional de quien reconoció la alerta. */
  acknowledgeNote: z4.string().nullable()
});
var acknowledgeAlertRequest = z4.object({
  note: z4.string().max(500).optional()
});
var listAlertsQuery = z4.object({
  status: alertStatus.optional(),
  severity: alertSeverity.optional(),
  nodeId: nodeId.optional(),
  ruleId: uuid.optional(),
  limit: z4.coerce.number().int().positive().max(500).default(100)
});
var alertNotice = z4.object({
  alertId: uuid,
  tenantId,
  ruleId: uuid,
  ruleName: alertRuleName,
  metric: alertMetric,
  severity: alertSeverity,
  /** `fired` estrena la alerta, `renotified` insiste, `resolved` avisa del alta. */
  kind: z4.enum(["fired", "renotified", "resolved"]),
  nodeId,
  nodeAlias: z4.string(),
  branchName: z4.string().nullable(),
  clientName: z4.string().nullable(),
  value: z4.number().nullable(),
  threshold: z4.number(),
  at: z4.coerce.date(),
  /** Canales a los que va. Se resuelve en el backend, no en el canal. */
  channelIds: z4.array(uuid),
  /** Ventana de agrupación heredada de la regla, en segundos. */
  groupWindowSeconds: z4.number().int().nonnegative()
});
function alertGroupKey(notice) {
  return `${notice.ruleId}|${notice.kind}|${notice.branchName ?? "-"}`;
}

// src/hierarchy.ts
import { z as z5 } from "zod";
var clientRecord = z5.object({
  id: clientId,
  tenantId,
  name: z5.string().min(1).max(160),
  notes: z5.string().max(500).nullable(),
  branchCount: z5.number().int().nonnegative().optional(),
  nodeCount: z5.number().int().nonnegative().optional()
});
var createClientRequest = z5.object({
  name: z5.string().min(1).max(160),
  notes: z5.string().max(500).optional()
});
var updateClientRequest = createClientRequest.partial();
var branchRecord = z5.object({
  id: branchId,
  tenantId,
  clientId,
  name: z5.string().min(1).max(160),
  address: z5.string().max(300).nullable(),
  nodeCount: z5.number().int().nonnegative().optional(),
  onlineNodeCount: z5.number().int().nonnegative().optional()
});
var createBranchRequest = z5.object({
  clientId,
  name: z5.string().min(1).max(160),
  address: z5.string().max(300).optional()
});
var updateBranchRequest = createBranchRequest.omit({ clientId: true }).partial();
var updateNodeRequest = z5.object({
  alias: nodeAlias.optional(),
  branchId: branchId.optional(),
  provider: z5.string().max(120).nullable().optional(),
  monthlyCostCents: z5.number().int().nonnegative().nullable().optional(),
  nodeType: nodeType.nullable().optional()
});
var hierarchyTree = z5.object({
  clients: z5.array(
    clientRecord.extend({
      branches: z5.array(
        branchRecord.extend({
          nodes: z5.array(
            z5.object({
              id: nodeId,
              alias: nodeAlias,
              status: nodeStatus,
              osFamily: osFamily.nullable()
            })
          )
        })
      )
    })
  )
});
var ENROLLMENT_LONG_MAX_EXPIRY_MINUTES = 10080;
var createEnrollmentTokenRequest = z5.object({
  clientId,
  branchId,
  suggestedAlias: nodeAlias.optional(),
  /** Vida del token. Corta a propósito: es una credencial de instalación. */
  expiresInMinutes: z5.number().int().positive().max(ENROLLMENT_LONG_MAX_EXPIRY_MINUTES).default(60)
});
var createEnrollmentTokenResponse = z5.object({
  /** Valor en claro. El OCC solo guarda su hash y no vuelve a mostrarlo. */
  token: z5.string(),
  expiresAt: z5.coerce.date(),
  installCommandLinux: z5.string(),
  installCommandWindows: z5.string(),
  /**
   * One-liner de alta para Windows 7 (plan de compatibilidad, F2.5).
   *
   * Opcional y anulable porque **no siempre se puede ofrecer**: lleva incrustados
   * el sha256 del instalador y el del `SHA256SUMS` de la versión que el canal
   * publica en ese momento, y sin esos tres datos el comando no existe. Ausente o
   * `null` significa que no hay comando, no que el panel deba improvisar uno: un
   * comando a medias se pega en la tienda y falla allí.
   */
  installCommandWindows7: z5.string().nullable().optional(),
  /**
   * Motivo legible por el que no hay comando de Windows 7, para pintarlo en el
   * sitio donde iría el comando. Presente solo cuando `installCommandWindows7`
   * viene vacío, y al revés: uno de los dos siempre trae algo.
   */
  installCommandWindows7Unavailable: z5.string().nullable().optional()
});
var enrollmentTokenFormat = z5.enum(["long", "short", "manual"]);
var ENROLLMENT_MAX_USES = 1e3;
var ENROLLMENT_MANUAL_CODE_MAX_LENGTH = 40;
var issueEnrollmentTokenRequest = createEnrollmentTokenRequest.extend({
  /**
   * Usos que admite. Uno si nadie pide otra cosa: el token para una caja
   * concreta se pide igual que siempre, y pedir más es explícito y visible en el
   * cuerpo de la petición.
   */
  maxUses: z5.number().int().min(1).max(ENROLLMENT_MAX_USES).default(1),
  /** Ausente significa `long`: quien ya llamaba a esta ruta no cambia de trato. */
  format: enrollmentTokenFormat.optional(),
  /**
   * El código que fija el administrador. Solo con `format: 'manual'`, que es el
   * único de los tres donde el secreto no lo sortea el servidor.
   *
   * Lo que hace adivinable a un código manual es el **destino** —`FRUTAL` es un
   * código malísimo para el cliente Frutal y razonable para cualquier otro—, así
   * que su rechazo se decide en el backend, con el nombre del cliente delante.
   * Aquí solo se acota el tamaño, para que un cuerpo absurdo no llegue a la base.
   */
  code: z5.string().min(1).max(ENROLLMENT_MANUAL_CODE_MAX_LENGTH).optional(),
  /**
   * Declara que el código manual lleva sufijo aleatorio (`FRUTAL-7K2M`).
   *
   * No es comprobable: mirando el texto no hay forma de saber si esos cuatro
   * caracteres los sorteó el panel o los eligió una persona. De la declaración
   * cuelgan los topes de caducidad, y por eso su ausencia se lee del lado
   * estricto —como pelado— en el backend.
   */
  randomSuffix: z5.boolean().optional(),
  /**
   * **Sin valor por defecto**, al contrario que en el esquema base: el que toca
   * depende del formato, y con un `default` puesto aquí no habría forma de
   * distinguir «no lo dijo» de «pidió una hora».
   */
  expiresInMinutes: z5.number().int().positive().max(ENROLLMENT_LONG_MAX_EXPIRY_MINUTES).optional()
});
var issueEnrollmentTokenResponse = createEnrollmentTokenResponse.extend({
  maxUses: z5.number().int().positive(),
  format: enrollmentTokenFormat
});
var enrollmentTokenSummary = z5.object({
  id: uuid,
  format: enrollmentTokenFormat,
  /** Destino: hacia dónde mete máquinas quien tenga este secreto. */
  clientId,
  clientName: z5.string(),
  branchId,
  branchName: z5.string(),
  suggestedAlias: z5.string().nullable(),
  expiresAt: z5.coerce.date(),
  maxUses: z5.number().int().positive(),
  /** Usos consumidos. Con `maxUses` da los que quedan, que es lo accionable. */
  usedCount: z5.number().int().nonnegative(),
  /** Último canje, o `null` si todavía no se ha usado. */
  lastUsedAt: z5.coerce.date().nullable(),
  issuedAt: z5.coerce.date(),
  issuedByUserId: userId,
  /**
   * Correo de quien lo emitió, unido en la consulta y no copiado al emitir.
   * `null` cuando ese operador ya no está: la credencial le sobrevive y hay que
   * poder verla igual — es justo el caso en el que uno quiere revocarla.
   */
  issuedByEmail: z5.string().nullable()
});
var listEnrollmentTokensResponse = z5.object({
  tokens: z5.array(enrollmentTokenSummary)
});
var revokeEnrollmentTokenRequest = z5.object({ tokenId: uuid });
var revokedEnrollmentToken = z5.object({
  token: enrollmentTokenSummary,
  revokedAt: z5.coerce.date()
});
var resolveEnrollmentCodeRequest = z5.object({
  code: z5.string().min(1).max(ENROLLMENT_MANUAL_CODE_MAX_LENGTH)
});
var enrollmentCodeCommands = z5.object({
  windows: z5.string(),
  linux: z5.string(),
  /**
   * Windows 7 puede no tener comando: lleva incrustados los hashes de la versión
   * que el canal publica en ese instante, y sin ellos no existe. `null` con el
   * motivo al lado, para pintarlo donde iría el comando — nunca uno a medias.
   */
  windows7: z5.string().nullable(),
  windows7Unavailable: z5.string().nullable()
});
var resolveEnrollmentCodeResponse = z5.object({
  clientName: z5.string(),
  branchName: z5.string(),
  /**
   * Alias que propuso quien emitió el código, o `null` si no propuso ninguno. En
   * ese caso el nombre lo pone el instalador con el hostname de la caja, que es
   * el mismo orden de preferencia que aplica el canje.
   */
  suggestedAlias: z5.string().nullable(),
  expiresAt: z5.coerce.date(),
  /** Máquinas que todavía puede enrolar este código. */
  remainingUses: z5.number().int().positive(),
  commands: enrollmentCodeCommands
});
var enrollmentCodeStatusRequest = resolveEnrollmentCodeRequest;
var enrollmentCodeEnrolledNode = z5.object({
  /** El nombre con el que se ve en el panel, que es lo que el técnico reconoce. */
  alias: z5.string(),
  at: z5.coerce.date()
});
var enrollmentCodeStatusResponse = z5.object({
  expiresAt: z5.coerce.date(),
  remainingUses: z5.number().int().nonnegative(),
  /**
   * ¿Admite todavía una máquina más?
   *
   * Un solo campo para las cuatro formas de dejar de servir —revocado, caducado,
   * agotado, inservible por fallos— porque lo que la página necesita saber es
   * cuándo dejar de preguntar. **Cuál de las cuatro** lo dice `resolve`, con el
   * texto ya redactado (§13.8), y esto no lo repite: la página tiene ahí el motivo
   * y aquí el interruptor.
   */
  live: z5.boolean(),
  enrolled: z5.array(enrollmentCodeEnrolledNode)
});
var ENROLLMENT_CODE_STATUS_MAX_NODES = 20;

// src/plugins.ts
import { z as z6 } from "zod";
var paramName = z6.string().min(1).max(64).regex(
  /^[A-Za-z_][A-Za-z0-9_]*$/,
  "Nombre de variable inv\xE1lido: letras, n\xFAmeros y guion bajo, sin empezar por n\xFAmero"
);
var pluginParamType = z6.enum(["string", "number", "boolean"]);
var pluginParam = z6.object({
  name: paramName,
  label: z6.string().max(120).optional(),
  type: pluginParamType,
  required: z6.boolean().default(false),
  /** Valor si el operador no lo rellena. No aplica si `required` es `true`. */
  default: z6.string().max(4e3).optional()
});
var pluginStep = z6.object({
  kind: z6.literal("exec"),
  shell: z6.enum(["powershell", "bash", "sh"]),
  script: z6.string().min(1).max(64e3),
  timeoutSeconds: z6.number().int().positive().max(3600).default(120),
  /** Si falla, seguir con el siguiente paso en vez de abortar el plugin. */
  continueOnError: z6.boolean().default(false)
});
var pluginSpecShape = z6.object({
  name: z6.string().min(1).max(160),
  description: z6.string().max(2e3).optional(),
  /** `undefined` = corre en cualquier sistema operativo. */
  osFamily: osFamily.optional(),
  paramsSchema: z6.array(pluginParam).max(50).default([]),
  steps: z6.array(pluginStep).min(1).max(50)
});
function refinePluginSpec(spec, ctx) {
  const seen = /* @__PURE__ */ new Set();
  for (const [index, param] of spec.paramsSchema.entries()) {
    if (seen.has(param.name)) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: ["paramsSchema", index, "name"],
        message: `El par\xE1metro ${param.name} est\xE1 repetido`
      });
    }
    seen.add(param.name);
    if (param.required && param.default !== void 0) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: ["paramsSchema", index, "default"],
        message: "Un par\xE1metro obligatorio no lleva valor por omisi\xF3n: nunca se usar\xEDa"
      });
    }
  }
}
var pluginSpec = pluginSpecShape.superRefine(refinePluginSpec);
var updatePluginRequest = pluginSpec;
var pluginView = z6.object({
  id: pluginId,
  tenantId,
  name: z6.string(),
  description: z6.string().nullable(),
  osFamily: osFamily.nullable(),
  version: z6.number().int(),
  paramsSchema: z6.array(pluginParam),
  steps: z6.array(pluginStep),
  createdByUserId: uuid.nullable(),
  createdAt: z6.coerce.date(),
  updatedAt: z6.coerce.date()
});
var setPluginEnablementRequest = z6.object({
  enabled: z6.boolean()
});
var pluginEnablementView = z6.object({
  pluginId,
  clientId,
  enabled: z6.boolean()
});

// src/tunnel.ts
import { z as z7 } from "zod";
var TUNNEL_PROTOCOL = "occ.tunnel.v1";
var streamKind = z7.enum(["terminal", "exec", "file.read", "file.write", "control"]);
var agentCapability = z7.enum([
  "terminal",
  "exec",
  "files",
  "services",
  "processes",
  "inventory",
  "rustdesk",
  /**
   * El agente sabe quitarse de la máquina por orden del OCC (`agent.uninstall`).
   * Va declarada como las demás porque un agente anterior a esta capacidad
   * existe y hay que poder distinguir «no sabe» de «falló»: la baja del nodo
   * dice `unsupported` en vez de dejar creer que la máquina quedó limpia.
   */
  "selfmanage",
  /**
   * El agente puede listar los contenedores Docker del nodo (`containers.list`).
   * Solo la declaran los nodos donde hay un `docker` instalado: es lo que evita
   * ofrecer —y vigilar— contenedores en una caja de caja registradora.
   */
  "containers"
]);
var resetCode = z7.enum([
  "unauthorized",
  "superseded",
  "protocol_error",
  "flow_violation",
  "unsupported",
  "internal"
]);
var tunnelEvent = z7.discriminatedUnion("type", [
  z7.object({
    type: z7.literal("connected"),
    tenantId,
    nodeId,
    agentVersion: z7.string(),
    capabilities: z7.array(agentCapability),
    at: z7.coerce.date()
  }),
  z7.object({
    type: z7.literal("disconnected"),
    tenantId,
    nodeId,
    reason: resetCode.or(z7.literal("closed")),
    at: z7.coerce.date()
  }),
  z7.object({
    type: z7.literal("heartbeat"),
    tenantId,
    nodeId,
    health: healthSample
  }),
  z7.object({
    type: z7.literal("inventory"),
    tenantId,
    nodeId,
    inventory: nodeInventory
  }),
  z7.object({
    type: z7.literal("service_changed"),
    tenantId,
    nodeId,
    service: z7.string(),
    status: z7.enum(["running", "stopped", "failed", "unknown"])
  }),
  z7.object({
    type: z7.literal("agent_updated"),
    tenantId,
    nodeId,
    fromVersion: z7.string(),
    toVersion: z7.string()
  })
]);
var eventsChannel = (tenant) => `occ:events:${tenant}`;
var EVENTS_CHANNEL_PATTERN = "occ:events:*";
var presenceRecord = z7.object({
  tenantId,
  nodeId,
  brokerInstance: z7.string(),
  connectedAt: z7.coerce.date(),
  lastSeenAt: z7.coerce.date(),
  agentVersion: z7.string(),
  capabilities: z7.array(agentCapability)
});
var presenceKey = (tenant, node) => `occ:presence:${tenant}:${node}`;

// src/actions.ts
var define = (action) => action;
var empty = z8.object({});
var ok = z8.object({ ok: z8.literal(true) });
var nodesList = define({
  name: "nodes.list",
  description: "Lista los nodos visibles dentro del alcance del actor.",
  scope: "tenant",
  minRole: "read_only",
  destructive: false,
  input: z8.object({
    clientId: clientId.optional(),
    branchId: branchId.optional(),
    status: z8.enum(["online", "offline", "unknown"]).optional(),
    search: z8.string().optional()
  }),
  output: z8.object({ nodes: z8.array(nodeSummary) })
});
var nodesGet = define({
  name: "nodes.get",
  description: "Estado, salud y capacidades declaradas de un nodo concreto.",
  scope: "node",
  minRole: "read_only",
  destructive: false,
  input: z8.object({ node: nodeRef }),
  /**
   * Las capacidades salen por aquí y no por una ruta aparte porque son la
   * respuesta a "qué se le puede pedir a *este* nodo": quien abre la ficha las
   * necesita para no ofrecer un botón que el agente rechazaría, y MCP para no
   * publicar una herramienta que ese nodo no sabe servir.
   *
   * Un array vacío significa "no se pudo preguntar" —el nodo no está conectado o
   * el broker no responde—, no "este agente no sabe hacer nada". Quien lo consuma
   * mira `node.status` para distinguirlo, que es el campo que ya lo dice.
   */
  output: z8.object({
    node: nodeSummary,
    health: healthSample.nullable(),
    capabilities: z8.array(agentCapability).default([])
  })
});
var nodesInventory = define({
  name: "nodes.inventory",
  description: "Inventario de hardware y software instalado del nodo.",
  scope: "node",
  minRole: "read_only",
  destructive: false,
  input: z8.object({ node: nodeRef }),
  output: z8.object({ inventory: nodeInventory })
});
var scriptRun = define({
  name: "script.run",
  description: "Ejecuta un comando o script en el nodo y devuelve salida y c\xF3digo de salida.",
  scope: "node",
  minRole: "support_technician",
  destructive: true,
  input: z8.object({
    node: nodeRef,
    /** Script inline o referencia a la biblioteca versionada. */
    script: z8.union([
      z8.object({ inline: z8.string().min(1), shell: z8.enum(["powershell", "bash", "sh"]) }),
      z8.object({ libraryId: z8.string().min(1), args: z8.record(z8.string()).default({}) })
    ]),
    timeoutSeconds: z8.number().int().positive().max(3600).default(120)
  }),
  output: z8.object({
    exitCode: z8.number().int(),
    stdout: z8.string(),
    stderr: z8.string(),
    truncated: z8.boolean()
  })
});
var servicesList = define({
  name: "services.list",
  description: "Lista los servicios del nodo y su estado.",
  scope: "node",
  minRole: "read_only",
  destructive: false,
  input: z8.object({ node: nodeRef }),
  output: z8.object({
    services: z8.array(
      z8.object({
        name: z8.string(),
        displayName: z8.string().optional(),
        status: z8.enum(["running", "stopped", "failed", "unknown"]),
        startType: z8.string().optional()
      })
    )
  })
});
var servicesControl = define({
  name: "services.control",
  description: "Arranca, detiene o reinicia un servicio del nodo.",
  scope: "node",
  minRole: "support_technician",
  destructive: true,
  input: z8.object({
    node: nodeRef,
    service: z8.string().min(1),
    action: z8.enum(["start", "stop", "restart"])
  }),
  output: z8.object({ status: z8.enum(["running", "stopped", "failed", "unknown"]) })
});
var nodeProcess = z8.object({
  pid: z8.number().int().nonnegative(),
  ppid: z8.number().int().nonnegative().optional(),
  name: z8.string(),
  /** Como lo da el sistema: `DOMINIO\\usuario` en Windows, el nombre suelto en Unix. */
  username: z8.string().optional(),
  /**
   * Acumulado desde que arrancó el proceso, no del último segundo, y **sin techo
   * en 100**: en una máquina de ocho núcleos un proceso puede acumular más. No se
   * limita a 100 como en `healthSample` porque ahí se mide la máquina y aquí un
   * proceso dentro de ella.
   */
  cpuPercent: z8.number().nonnegative().optional(),
  memoryBytes: z8.number().int().nonnegative().optional(),
  startedAt: z8.coerce.date().optional()
});
var processesList = define({
  name: "processes.list",
  description: "Lista los procesos del nodo, del que m\xE1s memoria consume al que menos.",
  scope: "node",
  minRole: "read_only",
  destructive: false,
  /**
   * Sin filtro ni tope, y no por descuido: el agente no los acepta. Una lista de
   * un Windows de escritorio son unos 1800 procesos y 300 kB de JSON, que caben de
   * sobra en la respuesta de un `control`. Declarar aquí un `limit` que el nodo
   * ignora sería peor que no tenerlo: el llamante creería estar pidiendo los
   * veinte primeros y recibiría todo igual.
   */
  input: z8.object({ node: nodeRef }),
  output: z8.object({ processes: z8.array(nodeProcess) })
});
var nodeContainer = z8.object({
  id: z8.string(),
  /** Sin la barra inicial que antepone Docker. */
  name: z8.string(),
  image: z8.string(),
  /** El `State` de Docker. Solo `running` cuenta como arriba. */
  state: z8.enum(["running", "restarting", "exited", "paused", "created", "dead", "removing"]),
  status: z8.string(),
  labels: z8.record(z8.string()).optional()
});
var containersList = define({
  name: "containers.list",
  description: 'Lista los contenedores Docker del nodo, corriendo o no. Solo en nodos cuyo agente declara la capacidad "containers".',
  scope: "node",
  minRole: "read_only",
  destructive: false,
  input: z8.object({ node: nodeRef }),
  output: z8.object({ containers: z8.array(nodeContainer) })
});
var watchesList = define({
  name: "watches.list",
  description: "Lista los procesos y contenedores que se vigilan en el nodo y c\xF3mo va cada uno seg\xFAn el \xFAltimo barrido (unknown no significa que est\xE9 bien: significa que a\xFAn no se midi\xF3).",
  scope: "node",
  // `support_technician` y no `read_only`: es lo que exige la API REST y el panel
  // para ver las vigilancias de un nodo; el catálogo no puede ser más permisivo que ellos.
  minRole: "support_technician",
  destructive: false,
  input: z8.object({ node: nodeRef }),
  output: z8.object({ watches: z8.array(nodeWatchView) })
});
var watchesSet = define({
  name: "watches.set",
  description: "Empieza a vigilar un proceso o contenedor del nodo: avisa si lleva 5 minutos seguidos sin correr. Si ya se vigilaba, actualiza sus canales y la reactiva si estaba pausada. Se identifica por nombre (no PID); un * al final es un prefijo. No puede pausar ni borrar. `channelIds` reemplaza los canales actuales: m\xE1ndalos siempre.",
  scope: "node",
  minRole: "support_technician",
  destructive: false,
  input: z8.object({
    node: nodeRef,
    kind: nodeWatchKind,
    target: nodeWatchTarget,
    // Sin `default([])` a diferencia de la API REST: aquí es un reemplazo, y omitirlo
    // por descuido vaciaría los canales de una vigilancia que sí avisaba.
    channelIds: z8.array(uuid).max(10)
  }),
  output: nodeWatchView
});
var processesKill = define({
  name: "processes.kill",
  description: "Termina un proceso del nodo por PID.",
  scope: "node",
  minRole: "support_technician",
  destructive: true,
  // `positive()` aquí sí, al contrario que en el listado: el pid 0 aparece en la
  // lista pero no se puede matar, y el agente lo rechaza igual.
  input: z8.object({ node: nodeRef, pid: z8.number().int().positive() }),
  output: ok
});
var filePath = z8.string().min(1).max(4096);
var sha256Hex = z8.string().regex(/^[0-9a-fA-F]{64}$/, "El sha256 va en 64 hexadecimales");
var filesList = define({
  name: "files.list",
  description: "Lista el contenido de un directorio del nodo.",
  scope: "node",
  minRole: "read_only",
  destructive: false,
  input: z8.object({ node: nodeRef, path: filePath }),
  output: z8.object({
    entries: z8.array(
      z8.object({
        name: z8.string(),
        path: filePath,
        kind: z8.enum(["file", "directory", "symlink", "other"]),
        sizeBytes: z8.number().int().nonnegative().optional(),
        modifiedAt: z8.coerce.date().optional()
      })
    )
  })
});
var filesDownload = define({
  name: "files.download",
  description: "Descarga un archivo del nodo. Devuelve una URL de staging con expiraci\xF3n.",
  scope: "node",
  minRole: "support_technician",
  destructive: false,
  input: z8.object({ node: nodeRef, path: filePath }),
  output: z8.object({
    downloadUrl: z8.string().url(),
    /**
     * Clave del objeto en el staging. Va aparte de la URL porque es lo que
     * `files.upload` vuelve a pedir: sin ella, lo que se acaba de bajar de un
     * nodo no se puede mandar a otro sin volver a subirlo desde el navegador.
     */
    stagingKey: z8.string().min(1),
    sizeBytes: z8.number().int().nonnegative(),
    sha256: sha256Hex,
    expiresAt: z8.coerce.date()
  })
});
var filesUpload = define({
  name: "files.upload",
  description: "Sube un archivo al nodo desde el staging del OCC.",
  scope: "node",
  minRole: "support_technician",
  destructive: true,
  input: z8.object({
    node: nodeRef,
    destinationPath: filePath,
    /**
     * Objeto del staging (MinIO/S3): lo deja ahí un `files.download` previo o
     * el panel al subir un archivo del disco del operador.
     */
    stagingKey: z8.string().min(1),
    /** Checksum esperado. El agente lo verifica antes de renombrar el temporal. */
    sha256: sha256Hex,
    overwrite: z8.boolean().default(false)
  }),
  output: z8.object({ bytesWritten: z8.number().int().nonnegative() })
});
var filesDelete = define({
  name: "files.delete",
  description: "Borra un archivo o directorio del nodo.",
  scope: "node",
  minRole: "support_technician",
  destructive: true,
  input: z8.object({ node: nodeRef, path: filePath, recursive: z8.boolean().default(false) }),
  output: ok
});
var terminalOpen = define({
  name: "terminal.open",
  description: "Abre una sesi\xF3n de terminal en el nodo a trav\xE9s del t\xFAnel inverso.",
  scope: "node",
  minRole: "support_technician",
  destructive: false,
  input: z8.object({
    node: nodeRef,
    cols: z8.number().int().positive().default(120),
    rows: z8.number().int().positive().default(30)
  }),
  /**
   * El vale va aparte de la URL y no dentro de ella a propósito: una URL con
   * credenciales acaba en los logs de cualquier proxy por el que pase. El
   * cliente abre el WebSocket y lo manda en el primer mensaje, igual que hace
   * el panel con su token en `/ws/events`.
   */
  output: z8.object({
    sessionId,
    websocketUrl: z8.string(),
    ticket: z8.string(),
    expiresAt: z8.coerce.date()
  })
});
var screenOpen = define({
  name: "screen.open",
  description: "Abre una sesi\xF3n de pantalla remota (RustDesk). El operador nunca ve la contrase\xF1a del nodo.",
  scope: "node",
  minRole: "support_technician",
  destructive: false,
  input: z8.object({
    node: nodeRef
  }),
  /**
   * Mismo reparto que `terminal.open`, y por el mismo motivo: la URL se puede
   * abrir en una pestaña y acabar en el historial o en los logs de un proxy, así
   * que el vale —que es lo que lleva la credencial del nodo detrás— viaja aparte
   * y el lanzador lo manda en el cuerpo de su primera petición.
   *
   * Cuando el modo es `attended`, esta respuesta solo existe si el usuario del
   * nodo ya aprobó: la aprobación se pide **antes** de abrir la sesión, no
   * después de devolver una URL que todavía no sirve.
   */
  output: z8.object({
    sessionId,
    launchUrl: z8.string(),
    ticket: z8.string(),
    expiresAt: z8.coerce.date()
  })
});
var nodesDecommission = define({
  name: "nodes.decommission",
  description: "Da de baja un nodo: desinstala el agente de la m\xE1quina, revoca su identidad y lo borra del OCC. Irreversible.",
  scope: "node",
  // No es `support_technician` aunque `files.delete` sí lo sea: aquello borra un
  // archivo de una máquina que sigue siendo nuestra, esto retira la máquina.
  minRole: "tenant_admin",
  destructive: true,
  input: z8.object({
    node: nodeRef,
    /**
     * El alias del nodo, escrito de nuevo por quien da la baja. No es
     * ceremonia: el borrado es irreversible y `node` puede venir de una fila de
     * una tabla que se ordenó de otra forma entre que se leyó y se pulsó.
     */
    confirmAlias: z8.string().min(1)
  }),
  output: z8.object({
    nodeId,
    alias: z8.string(),
    /**
     * Qué pasó con el agente de la máquina:
     *
     * - `done`: el nodo estaba conectado, recibió la orden y se está quitando.
     * - `pending`: el nodo no estaba alcanzable. La orden queda anotada y se le
     *   entrega en cuanto vuelva a conectarse, que es el caso normal —una
     *   máquina que se da de baja suele estar ya apagada.
     * - `unsupported`: el agente es anterior a esta capacidad y no sabe
     *   quitarse. Hay que pasar por la máquina con `install.ps1 -Uninstall`.
     */
    uninstall: z8.enum(["done", "pending", "unsupported"])
  })
});
var sessionsListActive = define({
  name: "sessions.listActive",
  description: "Sesiones de soporte activas dentro del alcance del actor.",
  scope: "tenant",
  minRole: "read_only",
  destructive: false,
  input: empty,
  output: z8.object({
    sessions: z8.array(
      z8.object({
        id: sessionId,
        nodeId,
        kind: z8.enum(["screen", "terminal"]),
        startedAt: z8.coerce.date(),
        operator: z8.string()
      })
    )
  })
});
var pluginsList = define({
  name: "plugins.list",
  description: "Lista los plugins de la biblioteca del tenant.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: false,
  input: empty,
  output: z8.object({ plugins: z8.array(pluginView) })
});
var pluginsGet = define({
  name: "plugins.get",
  description: "Detalle de un plugin de la biblioteca, con sus pasos completos.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: false,
  input: z8.object({ pluginId }),
  output: pluginView
});
var pluginsCreate = define({
  name: "plugins.create",
  description: "Da de alta un plugin nuevo en la biblioteca del tenant. Nace deshabilitado para todo cliente: habilitarlo es un paso aparte.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: true,
  input: pluginSpec,
  output: pluginView
});
var pluginsUpdate = define({
  name: "plugins.update",
  description: "Reemplaza el contenido de un plugin existente (par\xE1metros y pasos incluidos). Sube la versi\xF3n si cambia el comportamiento.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: true,
  input: z8.object({ pluginId }).merge(pluginSpecShape).superRefine(refinePluginSpec),
  output: pluginView
});
var pluginsDelete = define({
  name: "plugins.delete",
  description: "Retira un plugin de la biblioteca. Falla si sigue habilitado para alg\xFAn cliente: hay que deshabilitarlo ah\xED primero.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: true,
  input: z8.object({ pluginId }),
  output: ok
});
var pluginsSetEnablement = define({
  name: "plugins.setEnablement",
  description: "Habilita o deshabilita un plugin para un cliente: decide si los nodos de ese cliente pueden llegar a correrlo.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: true,
  input: z8.object({ pluginId, clientId, enabled: z8.boolean() }),
  output: pluginEnablementView
});
var enrollmentIssue = define({
  name: "enrollment.issue",
  description: "Emite una credencial de instalaci\xF3n para una sucursal: un c\xF3digo corto de seis caracteres para teclear en la m\xE1quina, un c\xF3digo fijado a mano, o el token largo para GPO e Intune. Devuelve el secreto en claro una sola vez, con los one-liners listos para pegar.",
  scope: "tenant",
  minRole: "tenant_admin",
  // Destructiva no porque borre nada, sino porque el resultado es una credencial
  // viva: quien la tenga mete máquinas en el portafolio de un cliente. Es lo que
  // el M11 quiere que se confirme antes, no después.
  destructive: true,
  input: issueEnrollmentTokenRequest,
  output: issueEnrollmentTokenResponse
});
var enrollmentList = define({
  name: "enrollment.list",
  description: "Lista los c\xF3digos y tokens de instalaci\xF3n vivos del tenant, con su destino, formato, caducidad y usos consumidos. Nunca devuelve el secreto ni su hash: no se pueden recuperar.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: false,
  input: empty,
  output: listEnrollmentTokensResponse
});
var enrollmentRevoke = define({
  name: "enrollment.revoke",
  description: "Revoca un c\xF3digo o token de instalaci\xF3n: deja de servir en el mismo instante. El identificador sale de enrollment.list.",
  scope: "tenant",
  minRole: "tenant_admin",
  destructive: true,
  input: revokeEnrollmentTokenRequest,
  output: revokedEnrollmentToken
});
var ACTIONS = {
  [nodesList.name]: nodesList,
  [nodesGet.name]: nodesGet,
  [nodesInventory.name]: nodesInventory,
  [scriptRun.name]: scriptRun,
  [servicesList.name]: servicesList,
  [servicesControl.name]: servicesControl,
  [processesList.name]: processesList,
  [processesKill.name]: processesKill,
  [containersList.name]: containersList,
  [watchesList.name]: watchesList,
  [watchesSet.name]: watchesSet,
  [filesList.name]: filesList,
  [filesDownload.name]: filesDownload,
  [filesUpload.name]: filesUpload,
  [filesDelete.name]: filesDelete,
  [terminalOpen.name]: terminalOpen,
  [screenOpen.name]: screenOpen,
  [nodesDecommission.name]: nodesDecommission,
  [sessionsListActive.name]: sessionsListActive,
  [pluginsList.name]: pluginsList,
  [pluginsGet.name]: pluginsGet,
  [pluginsCreate.name]: pluginsCreate,
  [pluginsUpdate.name]: pluginsUpdate,
  [pluginsDelete.name]: pluginsDelete,
  [pluginsSetEnablement.name]: pluginsSetEnablement,
  [enrollmentIssue.name]: enrollmentIssue,
  [enrollmentList.name]: enrollmentList,
  [enrollmentRevoke.name]: enrollmentRevoke
};
var actionNames = Object.keys(ACTIONS);
var isActionName = (value) => value in ACTIONS;

// src/agent-updates.ts
import { z as z9 } from "zod";
var AGENT_RELEASE_CHANNELS = ["stable", "beta"];
var agentReleaseChannel = z9.enum(AGENT_RELEASE_CHANNELS);
var agentVersion = z9.string().regex(/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/, "La versi\xF3n del agente debe ser semver");
var agentArtifact = z9.object({
  /**
   * Nombre del archivo publicado (`occ-agent-windows-amd64.exe`). Opcional
   * porque se puede derivar de `url`, y hay manifiestos anteriores al campo.
   */
  name: z9.string().min(1).optional(),
  /**
   * Qué binario es. El manifiesto del canal solo lleva `agent` y, para
   * windows/{amd64,arm64}, `rustdesk`: si listara el broker, un nodo
   * linux/amd64 podría "actualizarse" a él. El broker se publica en la release
   * y se verifica por `SHA256SUMS`, pero no entra en el canal.
   *
   * `rustdesk` es el instalador oficial de RustDesk (M4): `install.ps1` lo baja
   * junto al agente porque el agente **no** lo descarga nunca por su cuenta
   * (apps/agent/internal/rustdesk/paths_windows.go) — es exactamente el patrón
   * que un EDR marca como malware, y ataría la pantalla remota a que la red del
   * cliente permita más que el 443 hacia el OCC. Es opcional: una release sin
   * él deja el agente instalado igual, solo que sin autoprovisión de pantalla.
   */
  component: z9.enum(["agent", "broker", "rustdesk"]).default("agent"),
  /**
   * Eje de **publicación**, no familia de sistema operativo.
   *
   * `windows7` es la única entrada que no es un `GOOS`: es la variante del
   * artefacto de Windows compilada con el fork `go-win7`, porque el toolchain
   * oficial dejó de producir binarios que arrancan en Windows 7 SP1. Los dos
   * artefactos son `GOOS=windows` por dentro y solo se distinguen por este campo
   * — que es justamente lo que hace que un nodo Win7 no se autoactualice al
   * binario normal y se quede con un servicio que no arranca (plan de
   * compatibilidad con Windows 7, §1.4).
   *
   * Ojo con no confundir este eje con `osFamily` de `node.ts`: un nodo Win7
   * sigue reportando `osFamily: 'windows'` en su inventario, porque *es* Windows.
   * Aquí se está diciendo qué compilación le toca descargar, no en qué sistema
   * corre.
   */
  os: z9.enum(["windows", "windows7", "linux", "darwin"]),
  arch: z9.enum(["amd64", "arm64", "386", "arm"]),
  /** `windows/amd64`. Redundante con os+arch y presente porque se lee. */
  platform: z9.string().min(3).optional(),
  /** Ruta pública, relativa al OCC: `/downloads/agent/1.4.2/occ-agent-…`. */
  url: z9.string().min(1),
  sha256: z9.string().regex(/^[0-9a-f]{64}$/, "El sha256 va en 64 hexadecimales en min\xFAscula"),
  sizeBytes: z9.number().int().nonnegative()
});
var agentReleaseManifest = z9.object({
  channel: agentReleaseChannel,
  version: agentVersion,
  /** Commit del que salieron los binarios. Desambigua una etiqueta repetida. */
  commit: z9.string().min(7).optional(),
  releasedAt: z9.string().datetime(),
  artifacts: z9.array(agentArtifact).min(1),
  /**
   * Ed25519 sobre la forma canónica del manifiesto —sin este campo, claves
   * ordenadas, sin espacios— con prefijo `ed25519:`. Protege la **lista** de
   * hashes; el hash de cada entrada protege el binario.
   */
  signature: z9.string().optional()
});
var latestAgentReleaseQuery = z9.object({
  tenantId: tenantId.optional(),
  nodeId: nodeId.optional(),
  /** Fuerza un canal. Solo para depurar: lo normal es que lo decida el tenant. */
  channel: agentReleaseChannel.optional(),
  currentVersion: z9.string().min(1).max(64).optional()
});
var latestAgentReleaseResponse = z9.object({
  channel: agentReleaseChannel,
  /**
   * El canal está detenido a propósito. Es el freno de mano de M13 §6: una
   * versión mala se corta poniendo esto a `true`, y ningún agente se mueve
   * —tampoco los que ya empezaron a preguntar— sin tener que despublicar nada.
   */
  paused: z9.boolean(),
  /** Cada cuánto volver a preguntar. Lo decide el servidor para poder frenar la flota. */
  checkAfterSeconds: z9.number().int().positive(),
  /** Nulo si el canal está detenido o todavía no publicó ninguna versión. */
  manifest: agentReleaseManifest.nullable()
});
var agentUpdateOutcome = z9.enum(["updated", "rolled_back", "failed"]);
var agentUpdateReport = z9.object({
  tenantId,
  nodeId,
  fromVersion: z9.string().min(1).max(64),
  toVersion: z9.string().min(1).max(64),
  outcome: agentUpdateOutcome,
  /** Por qué falló o por qué retrocedió. Obligatorio de facto en esos dos casos. */
  reason: z9.string().max(500).optional()
});

// src/audit.ts
import { z as z10 } from "zod";
var auditOutcome = z10.enum(["success", "denied", "error"]);
var auditChannel = z10.enum(["panel", "mcp", "api", "system"]);
var auditEvent = z10.object({
  tenantId,
  actorUserId: userId.nullable(),
  channel: auditChannel,
  /** Nombre de la acción del catálogo, o un evento propio del OCC. */
  action: z10.string().min(1),
  nodeId: nodeId.nullable().default(null),
  sessionId: sessionId.nullable().default(null),
  outcome: auditOutcome,
  /** Motivo cuando `outcome` no es `success`. */
  reason: z10.string().nullable().default(null),
  durationMs: z10.number().int().nonnegative().nullable().default(null),
  /**
   * Detalle específico de la acción (ruta del archivo, nombre del servicio,
   * comando ejecutado). Nunca debe contener secretos.
   */
  detail: z10.record(z10.unknown()).default({}),
  occurredAt: z10.coerce.date()
});
var SYSTEM_AUDIT_ACTIONS = [
  "auth.login",
  "auth.logout",
  "auth.mfa_failed",
  "enrollment.token_issued",
  /**
   * Se emitió un **código** de instalación, corto o fijado a mano (plan de
   * enrolamiento por código, §11).
   *
   * Aparte de `enrollment.token_issued` y no como un detalle suyo porque el filtro
   * de la bitácora es coincidencia exacta sobre `action` (ver `listAuditQuery`):
   * "qué códigos se emitieron esta semana" tiene que ser una consulta por índice y
   * no un recorrido leyendo `detail`. Y porque no son la misma credencial — un
   * código de seis caracteres se resuelve desde una página pública y se dicta por
   * teléfono; un token de 41 se pega en un script.
   */
  "enrollment.code_issued",
  /**
   * Alguien apagó un código o un token antes de que caducara.
   *
   * Es la contrapartida obligada de que un código sea resoluble sin sesión: un
   * secreto filtrado se cierra revocándolo, y quién lo cerró y cuándo es parte de
   * la reconstrucción del incidente. El detalle lleva el destino y los usos que
   * llevaba gastados, nunca el hash.
   */
  "enrollment.token_revoked",
  "enrollment.node_enrolled",
  /** Credencial personal MCP creada (RF-13, autoservicio). */
  "mcp_credential.created",
  /** Credencial personal MCP revocada — a mano o por baja del dueño. */
  "mcp_credential.revoked",
  "hierarchy.changed",
  "permissions.changed",
  /** Branding del tenant (RF-21): cambia lo que ve todo el mundo al entrar. */
  "branding.changed",
  "session.consent_granted",
  "session.consent_denied",
  /**
   * Alguien cerró una sesión de soporte desde el panel. Se audita aparte del
   * cierre que hace el gateway cuando se le rompe el puente: uno es una decisión
   * de una persona —a veces sobre la sesión de otra— y el otro es un final de
   * transporte.
   */
  "session.closed",
  /**
   * Se emitió una URL firmada para subir un archivo al staging (M6.3). Queda
   * aparte de `files.upload`: entre las dos puede no haber nunca una subida, y un
   * vale pedido y no usado también es información.
   */
  "files.staging",
  "agent.updated",
  /**
   * Se le entregó a un agente la orden de desinstalarse de una baja que quedó
   * pendiente (`nodes.decommission` con el nodo apagado).
   *
   * Va aparte de la acción del catálogo porque ocurre **sin nadie delante**: la
   * baja la pidió un administrador hace días y esto pasa cuando la máquina vuelve
   * a encenderse. Sin esta entrada, la bitácora diría que se ordenó la baja y no
   * diría nunca si la máquina llegó a limpiarse.
   */
  "node.uninstall_delivered",
  // ── Alertas y notificaciones (M7, M12) ────────────────────────────────────
  "alert.rule_changed",
  /**
   * La alerta se encendió. Va a la bitácora y no solo a la tabla de alertas
   * porque el "por qué" de una intervención empieza aquí: sin la fila de
   * auditoría, un script ejecutado a las 3 de la mañana no tiene causa visible.
   */
  "alert.fired",
  "alert.resolved",
  "alert.acknowledged",
  "notification.channel_changed",
  /** Un envío fallido se audita igual que uno bueno: es el que hay que explicar. */
  "notification.sent",
  "notification.failed",
  // ── Grabaciones y retención (M10) ─────────────────────────────────────────
  "recording.started",
  "recording.stored",
  /** Alguien pidió reproducir una grabación. Ver la prueba también deja rastro. */
  "recording.played",
  "recording.purged",
  "retention.policy_changed"
];
var auditEntry = z10.object({
  id: uuid,
  tenantId,
  actorUserId: userId.nullable(),
  actorEmail: z10.string().nullable(),
  channel: auditChannel,
  action: z10.string().min(1),
  nodeId: nodeId.nullable(),
  nodeAlias: z10.string().nullable(),
  sessionId: sessionId.nullable(),
  outcome: auditOutcome,
  reason: z10.string().nullable(),
  durationMs: z10.number().int().nonnegative().nullable(),
  detail: z10.record(z10.unknown()),
  occurredAt: z10.coerce.date()
});
var listAuditQuery = z10.object({
  nodeId: nodeId.optional(),
  actorUserId: userId.optional(),
  sessionId: sessionId.optional(),
  action: z10.string().min(1).max(64).optional(),
  outcome: auditOutcome.optional(),
  channel: auditChannel.optional(),
  /** Desde (inclusive), sobre `occurredAt`. */
  from: z10.coerce.date().optional(),
  /** Hasta (inclusive), sobre `occurredAt`. */
  to: z10.coerce.date().optional(),
  limit: z10.coerce.number().int().positive().max(200).default(50),
  /**
   * Cursor de la página anterior. **Opaco**: se reenvía tal cual, no se
   * interpreta ni se construye en el cliente. Su formato es asunto del backend
   * y puede cambiar sin romper a nadie.
   */
  cursor: z10.string().min(1).max(200).optional()
});
var auditPage = z10.object({
  entries: z10.array(auditEntry),
  /** Cursor para pedir la siguiente página, o `null` cuando no hay más. */
  nextCursor: z10.string().nullable()
});

// src/auth.ts
import { z as z11 } from "zod";
var loginRequest = z11.object({
  email: z11.string().email(),
  password: z11.string().min(1),
  /** Código TOTP, cuando el usuario ya sabe que su cuenta tiene MFA. */
  totp: z11.string().regex(/^\d{6}$/).optional()
});
var sessionTokens = z11.object({
  accessToken: z11.string(),
  refreshToken: z11.string(),
  expiresInSeconds: z11.number().int().positive()
});
var loginResponse = z11.discriminatedUnion("status", [
  z11.object({
    status: z11.literal("authenticated"),
    tokens: sessionTokens
  }),
  z11.object({
    status: z11.literal("mfa_required"),
    /** Token de un solo uso y vida corta que solo sirve para canjear el TOTP. */
    mfaToken: z11.string()
  })
]);
var mfaVerifyRequest = z11.object({
  mfaToken: z11.string().min(1),
  totp: z11.string().regex(/^\d{6}$/)
});
var refreshRequest = z11.object({ refreshToken: z11.string().min(1) });
var accessTokenClaims = z11.object({
  sub: userId,
  tid: tenantId,
  email: z11.string().email(),
  perms: z11.array(permission),
  mfa: z11.boolean(),
  iat: z11.number().int(),
  exp: z11.number().int()
});
var currentUser = z11.object({
  id: userId,
  tenantId,
  email: z11.string().email(),
  name: z11.string(),
  mfaEnabled: z11.boolean(),
  permissions: z11.array(permission)
});

// src/branding.ts
import { z as z12 } from "zod";
var HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
var FUNCTIONAL_COLOR = /^(?:rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch)\(\s*[0-9a-zA-Z.%,/\s+-]{1,56}\)$/;
var NAMED_COLOR = /^[a-zA-Z]{3,24}$/;
var isCssColor = (value) => HEX_COLOR.test(value) || FUNCTIONAL_COLOR.test(value) || NAMED_COLOR.test(value);
var brandingColor = z12.string().trim().max(64).refine((value) => value === "" || isCssColor(value), {
  message: "Color no admitido: se acepta hexadecimal (#2563eb), una funci\xF3n de color (oklch(...), rgb(...)) o un nombre CSS"
});
var LOGO_MAX_BYTES = 256 * 1024;
var LOGO_MAX_CHARS = Math.ceil(LOGO_MAX_BYTES / 3) * 4 + 64;
var brandingSpec = z12.object({
  /** Sustituye «Omnia Control Center» en la barra lateral. Vacío = el nombre por omisión. */
  productName: z12.string().trim().max(60).default(""),
  /**
   * `https://…` o un `data:image/…;base64,…`. Los bytes de un `data:` los
   * comprueba el backend —el tipo declarado tiene que coincidir con lo que hay
   * dentro—, porque eso ya no es forma de datos sino contenido.
   */
  logoUrl: z12.string().trim().max(LOGO_MAX_CHARS).default(""),
  colors: z12.object({
    brand: brandingColor.default(""),
    surfaceLight: brandingColor.default(""),
    surfaceDark: brandingColor.default(""),
    textLight: brandingColor.default(""),
    textDark: brandingColor.default("")
  }).default({})
});
var brandingView = brandingSpec;
var emptyBranding = () => brandingSpec.parse({});
var hasBranding = (branding) => branding.productName !== "" || branding.logoUrl !== "" || Object.values(branding.colors).some((color) => color !== "");

// src/health-series.ts
import { z as z13 } from "zod";
var healthResolution = z13.enum(["raw", "5m", "1h"]);
var healthConnectionStatus = z13.enum(["online", "offline", "unknown"]);
var HORA = 36e5;
var DIA = 24 * HORA;
var HEALTH_RESOLUTIONS = {
  raw: { bucketMs: null, retentionMs: 2 * DIA, aggregatesFrom: null, label: "cada latido" },
  "5m": { bucketMs: 5 * 6e4, retentionMs: 30 * DIA, aggregatesFrom: "raw", label: "cada 5 min" },
  // 400 días y no 365: pasar del año es lo que permite comparar «este noviembre»
  // con «el noviembre anterior» sin que el dato haya desaparecido justo antes de
  // la comparación.
  "1h": { bucketMs: HORA, retentionMs: 400 * DIA, aggregatesFrom: "5m", label: "cada hora" }
};
var HEALTH_RESOLUTION_ORDER = ["raw", "5m", "1h"];
var LIMITE_POR_NIVEL = [
  { resolution: "raw", maxRangeMs: 6 * HORA },
  { resolution: "5m", maxRangeMs: 8 * DIA }
];
function chooseHealthResolution(from, to) {
  const rangeMs = Math.max(0, to.getTime() - from.getTime());
  for (const limite of LIMITE_POR_NIVEL) {
    if (rangeMs <= limite.maxRangeMs) {
      return limite.resolution;
    }
  }
  return "1h";
}
function healthBucketStart(at, resolution) {
  const bucketMs = HEALTH_RESOLUTIONS[resolution].bucketMs;
  if (bucketMs === null) {
    return at;
  }
  return new Date(Math.floor(at.getTime() / bucketMs) * bucketMs);
}
var healthSeriesPoint = healthSample.extend({
  /** Latidos que representa. `1` en `raw`. */
  sampleCount: z13.number().int().positive().default(1),
  connectionStatus: healthConnectionStatus.default("unknown"),
  /** Pico del tramo. Igual a la media en `raw`, donde el tramo es un latido. */
  cpuPercentMax: z13.number().min(0).max(100).optional(),
  memoryUsedPercentMax: z13.number().min(0).max(100).optional(),
  diskUsedPercentMax: z13.number().min(0).max(100).optional(),
  /**
   * Qué volumen era el más lleno. Sin esto, una subida de 40 % a 95 % puede ser
   * el disco llenándose o que el volumen más lleno pasó a ser otro, y son dos
   * incidentes distintos.
   */
  diskMountpoint: z13.string().optional(),
  memoryUsedBytes: z13.number().int().nonnegative().optional(),
  memoryTotalBytes: z13.number().int().nonnegative().optional()
});
var healthSeriesResponse = z13.object({
  nodeId,
  /** Nivel realmente servido. */
  resolution: healthResolution,
  /** `true` si se sirvió un nivel más grueso del pedido porque el fino ya venció. */
  degraded: z13.boolean().default(false),
  from: z13.coerce.date(),
  to: z13.coerce.date(),
  /**
   * Memoria total del nodo, del inventario. Va en el envoltorio y no en cada
   * punto porque es una propiedad de la máquina, no de la muestra: repetirla
   * 720 veces sería transportar el mismo número una vez por punto.
   */
  memoryTotalBytes: z13.number().int().nonnegative().nullable(),
  samples: z13.array(healthSeriesPoint)
});
var healthSeriesQuery = z13.object({
  from: z13.coerce.date().optional(),
  to: z13.coerce.date().optional(),
  resolution: healthResolution.optional()
});
var HEALTH_SERIES_DEFAULT_RANGE_MS = 6 * HORA;
var HEALTH_SERIES_MAX_POINTS = 5e3;

// src/mcp.ts
import { zodToJsonSchema } from "zod-to-json-schema";
var separatorPattern = /\./g;
var toolNameFor = (action) => action.replace(separatorPattern, "_");
var byToolName = new Map(
  Object.keys(ACTIONS).map((action) => [toolNameFor(action), action])
);
if (byToolName.size !== Object.keys(ACTIONS).length) {
  throw new Error("Dos acciones del cat\xE1logo producen el mismo nombre de herramienta MCP");
}
var actionForToolName = (tool) => byToolName.get(tool);
var CONFIRM_ARGUMENT = "confirm";
function withConfirmation(schema) {
  return {
    ...schema,
    properties: {
      ...schema.properties ?? {},
      [CONFIRM_ARGUMENT]: {
        type: "boolean",
        const: true,
        description: "Debe ser true. Confirma que el usuario aprob\xF3 expl\xEDcitamente esta operaci\xF3n destructiva."
      }
    },
    required: [...schema.required ?? [], CONFIRM_ARGUMENT]
  };
}
function describe(action) {
  if (!action.destructive) return action.description;
  return `${action.description} OPERACI\xD3N DESTRUCTIVA: requiere aprobaci\xF3n expl\xEDcita del usuario y el argumento ${CONFIRM_ARGUMENT}=true.`;
}
function buildToolList() {
  return Object.keys(ACTIONS).map((name) => {
    const action = ACTIONS[name];
    const schema = zodToJsonSchema(action.input, { target: "jsonSchema7" });
    return {
      name: toolNameFor(name),
      description: describe(action),
      inputSchema: action.destructive ? withConfirmation(schema) : schema
    };
  });
}
function resolveToolCall(toolName, rawArguments) {
  const action = actionForToolName(toolName);
  if (action === void 0) {
    return { ok: false, error: `Herramienta desconocida: ${toolName}` };
  }
  const definition = ACTIONS[action];
  const args = typeof rawArguments === "object" && rawArguments !== null ? { ...rawArguments } : {};
  if (definition.destructive) {
    const confirmed = args[CONFIRM_ARGUMENT];
    if (confirmed !== true) {
      return {
        ok: false,
        error: `La acci\xF3n ${action} es destructiva y no se ejecut\xF3. Explica al usuario qu\xE9 va a pasar, pide su aprobaci\xF3n y vuelve a llamar con ${CONFIRM_ARGUMENT}=true.`
      };
    }
  }
  delete args[CONFIRM_ARGUMENT];
  const parsed = definition.input.safeParse(args);
  if (!parsed.success) {
    return { ok: false, error: `Argumentos inv\xE1lidos para ${action}: ${parsed.error.toString()}` };
  }
  return { ok: true, action, input: parsed.data };
}

// src/mcp-credentials.ts
import { z as z14 } from "zod";
var createMcpCredentialRequest = z14.object({
  name: z14.string().trim().min(1).max(64)
});
var createMcpCredentialResponse = z14.object({
  id: z14.string().uuid(),
  name: z14.string(),
  /** Valor en claro. Solo aparece en esta respuesta; después solo queda el hash. */
  token: z14.string(),
  createdAt: z14.coerce.date()
});
var mcpCredentialSummary = z14.object({
  id: z14.string().uuid(),
  name: z14.string(),
  createdAt: z14.coerce.date(),
  lastUsedAt: z14.coerce.date().nullable(),
  revokedAt: z14.coerce.date().nullable()
});
var listMcpCredentialsResponse = z14.object({
  credentials: z14.array(mcpCredentialSummary)
});

// src/notifications.ts
import { z as z15 } from "zod";
var notificationChannelKind = z15.enum(["slack", "whatsapp", "email", "discord"]);
var slackChannelConfig = z15.object({
  kind: z15.literal("slack"),
  /** Nombre del canal de Slack, para mostrar. No decide el destino. */
  channel: z15.string().max(80).optional()
});
var whatsappChannelConfig = z15.object({
  kind: z15.literal("whatsapp"),
  /** Identificador del número emisor en la cuenta de WhatsApp Business. */
  phoneNumberId: z15.string().min(1).max(64).optional(),
  recipients: z15.array(z15.string().regex(/^[1-9]\d{6,14}$/, 'N\xFAmero E.164 sin "+"')).min(1).max(20),
  templateName: z15.string().min(1).max(120),
  /** Idioma de la plantilla tal como está aprobada en Meta. */
  templateLanguage: z15.string().min(2).max(10).default("es")
});
var emailChannelConfig = z15.object({
  kind: z15.literal("email"),
  recipients: z15.array(z15.string().email()).min(1).max(50),
  /** Remitente. Si falta, se usa el de la instalación (`SMTP_FROM`). */
  from: z15.string().email().optional(),
  /** Prefijo del asunto, para reglas de bandeja de entrada. */
  subjectPrefix: z15.string().max(40).optional()
});
var discordChannelConfig = z15.object({
  kind: z15.literal("discord"),
  /** Nombre del servidor/canal de Discord, para mostrar. No decide el destino. */
  label: z15.string().max(80).optional()
});
var notificationChannelConfig = z15.discriminatedUnion("kind", [
  slackChannelConfig,
  whatsappChannelConfig,
  emailChannelConfig,
  discordChannelConfig
]);
var notificationChannelSecret = z15.union([
  z15.object({
    kind: z15.literal("slack"),
    /** URL del Incoming Webhook. Es la credencial: quien la tiene, publica. */
    webhookUrl: z15.string().url().startsWith("https://", "El webhook de Slack va por https")
  }),
  z15.object({
    kind: z15.literal("whatsapp"),
    /** Token de acceso permanente de la app de Meta. */
    accessToken: z15.string().min(20)
  }),
  z15.object({
    kind: z15.literal("email"),
    /** Contraseña del relay, si este canal usa uno propio en vez del de la instalación. */
    password: z15.string().min(1)
  }),
  z15.object({
    kind: z15.literal("discord"),
    /** URL del Incoming Webhook. Igual que Slack: quien la tiene, publica. */
    webhookUrl: z15.string().url().startsWith("https://", "El webhook de Discord va por https")
  })
]);
var createNotificationChannelRequest = z15.object({
  name: z15.string().min(1).max(120),
  enabled: z15.boolean().default(true),
  config: notificationChannelConfig,
  /**
   * Credencial propia del canal. Omitirla es válido y significa "usa la de la
   * instalación": es el caso normal del correo, donde el relay SMTP es uno para
   * todo el OCC.
   */
  secret: notificationChannelSecret.optional(),
  /** Gravedad mínima que sale por este canal. Un canal de WhatsApp puede querer solo `critical`. */
  minSeverity: alertSeverity.default("info")
});
var updateNotificationChannelRequest = z15.object({
  name: z15.string().min(1).max(120).optional(),
  enabled: z15.boolean().optional(),
  config: notificationChannelConfig.optional(),
  secret: notificationChannelSecret.optional(),
  minSeverity: alertSeverity.optional()
});
var notificationChannelView = z15.object({
  id: uuid,
  tenantId,
  kind: notificationChannelKind,
  name: z15.string(),
  enabled: z15.boolean(),
  config: notificationChannelConfig,
  minSeverity: alertSeverity,
  /** Si tiene credencial propia. El valor no sale nunca; sí si existe. */
  hasOwnSecret: z15.boolean(),
  lastDeliveryAt: z15.coerce.date().nullable(),
  lastDeliveryStatus: z15.enum(["sent", "failed"]).nullable(),
  lastDeliveryError: z15.string().nullable(),
  createdAt: z15.coerce.date(),
  updatedAt: z15.coerce.date()
});
var notificationMessage = z15.object({
  /** Asunto o primera línea. Cabe en la notificación del móvil. */
  title: z15.string().min(1).max(200),
  /** Cuerpo en texto llano. Es el mínimo común de los tres canales. */
  body: z15.string().min(1),
  severity: alertSeverity,
  /** Cuántos avisos vienen agrupados en este mensaje. */
  count: z15.number().int().positive(),
  /** Enlace al panel, cuando `BACKEND_PUBLIC_URL` permite construirlo. */
  url: z15.string().url().optional(),
  /** Líneas de detalle, una por nodo afectado. Slack las pinta como lista. */
  lines: z15.array(z15.string()).default([])
});
var deliveryStatus = z15.enum(["sent", "failed"]);
var notificationDeliveryView = z15.object({
  id: uuid,
  channelId: uuid,
  channelKind: notificationChannelKind,
  status: deliveryStatus,
  attempts: z15.number().int().positive(),
  error: z15.string().nullable(),
  /** Avisos que iban en el mensaje. Uno si no se agrupó nada. */
  noticeCount: z15.number().int().positive(),
  sentAt: z15.coerce.date().nullable(),
  createdAt: z15.coerce.date()
});

// src/recordings.ts
import { z as z16 } from "zod";
var recordingKind = z16.enum(["terminal", "screen"]);
var recordingFormat = z16.enum(["asciicast-v2", "webm"]);
var recordingStatus = z16.enum(["recording", "stored", "empty", "failed", "purged"]);
var recordingView = z16.object({
  id: uuid,
  tenantId,
  sessionId,
  nodeId: nodeId.nullable(),
  nodeAlias: z16.string().nullable(),
  operatorUserId: userId.nullable(),
  operatorEmail: z16.string().nullable(),
  kind: recordingKind,
  format: recordingFormat,
  status: recordingStatus,
  sizeBytes: z16.number().int().nonnegative(),
  durationMs: z16.number().int().nonnegative().nullable(),
  startedAt: z16.coerce.date(),
  endedAt: z16.coerce.date().nullable(),
  retentionUntil: z16.coerce.date().nullable(),
  purgedAt: z16.coerce.date().nullable()
});
var recordingPlayback = z16.object({
  recordingId: uuid,
  format: recordingFormat,
  /** URL firmada contra MinIO/S3. Caduca. */
  url: z16.string().url(),
  expiresAt: z16.coerce.date(),
  sizeBytes: z16.number().int().nonnegative()
});
var listRecordingsQuery = z16.object({
  sessionId: sessionId.optional(),
  nodeId: nodeId.optional(),
  kind: recordingKind.optional(),
  status: recordingStatus.optional(),
  limit: z16.coerce.number().int().positive().max(500).default(100)
});
var retentionPolicySpec = z16.object({
  /** Días que se conserva una grabación nueva. */
  retainDays: z16.number().int().positive().max(3650),
  legalHold: z16.boolean().default(false)
});
var retentionPolicyView = z16.object({
  tenantId,
  retainDays: z16.number().int().positive(),
  legalHold: z16.boolean(),
  /** `true` cuando el tenant no tiene política propia y hereda la de la instalación. */
  inherited: z16.boolean(),
  updatedAt: z16.coerce.date().nullable()
});
var recordingChunk = z16.object({
  tenantId,
  sessionId,
  seq: z16.number().int().nonnegative(),
  /** Líneas asciicast v2, sin el salto de línea final. */
  events: z16.array(z16.string()).min(1).max(5e3),
  /** `true` en el último trozo: cierra la grabación y dispara la subida final. */
  last: z16.boolean().default(false)
});
function asciicastHeaderLine(header) {
  return JSON.stringify(header);
}
function asciicastEventLine(offsetMs, stream, data) {
  const seconds = Math.max(0, Math.round(offsetMs)) / 1e3;
  return JSON.stringify([Number(seconds.toFixed(3)), stream, data]);
}
function asciicastFile(header, events) {
  return `${asciicastHeaderLine(header)}
${events.join("\n")}
`;
}

// src/scheduled-tasks.ts
import { z as z17 } from "zod";
var scheduledTaskId = uuid.describe("Tarea programada (M14).");
var scheduledTaskRunId = uuid.describe("Corrida de una tarea programada.");
var scheduledTaskScopeType = z17.enum(["client", "branch", "node"]);
var offlinePolicy = z17.enum(["skip", "retry_on_reconnect"]);
var scheduledTaskStatus = z17.enum(["draft", "active", "paused"]);
var scheduleSpec = z17.discriminatedUnion("kind", [
  z17.object({
    kind: z17.literal("interval"),
    /**
     * Piso de 15 minutos: por debajo de eso no hace falta un programador de
     * tareas, hace falta monitoreo (M7), que ya corre cada latido.
     */
    everyMinutes: z17.number().int().min(15).max(43200)
    // hasta 30 días
  }),
  z17.object({
    kind: z17.literal("weekly"),
    /** 0 = domingo, como `Date.getDay()`. */
    daysOfWeek: z17.array(z17.number().int().min(0).max(6)).min(1).max(7),
    hour: z17.number().int().min(0).max(23),
    minute: z17.number().int().min(0).max(59)
  }),
  z17.object({
    kind: z17.literal("once"),
    // Cadena ISO y no `z.coerce.date()` a propósito: esto viaja tal cual al
    // `jsonb` de `scheduled_tasks.schedule_spec` y vuelve tal cual en la
    // respuesta — un `Date` se serializa a la misma cadena en el camino de
    // vuelta, así que coercionar aquí solo crea un desajuste de tipos contra
    // la entidad sin ganar nada.
    runAt: z17.string().refine((value) => !Number.isNaN(Date.parse(value)), "Fecha inv\xE1lida")
  })
]);
var createScheduledTaskRequest = z17.object({
  pluginId,
  scopeType: scheduledTaskScopeType,
  scopeId: uuid,
  /** Sobrescribe parámetros del plugin. Mismo formato que `script.run`: nombre → valor de texto. */
  paramsOverrides: z17.record(z17.string()).default({}),
  scheduleSpec,
  /**
   * Sin default a propósito: a diferencia de una regla de alerta (que solo
   * vigila), una tarea programada puede ejecutar código en una flota entera.
   * Que arranque activa o se guarde como borrador es una decisión que tiene
   * que tomar quien la crea, no un valor que se cuela por omisión.
   */
  status: scheduledTaskStatus,
  offlinePolicy: offlinePolicy.default("retry_on_reconnect"),
  /** Solo aplica con `retry_on_reconnect`. */
  retryWindowHours: z17.number().int().positive().max(168).default(24),
  staggerSeconds: z17.number().int().min(0).max(3600).default(30),
  /** Canales que reciben el resumen de cada corrida. Vacío = el resultado solo queda en la bitácora. */
  channelIds: z17.array(uuid).max(20).default([])
});
var updateScheduledTaskRequest = z17.object({
  paramsOverrides: z17.record(z17.string()).optional(),
  scheduleSpec: scheduleSpec.optional(),
  status: scheduledTaskStatus.optional(),
  offlinePolicy: offlinePolicy.optional(),
  retryWindowHours: z17.number().int().positive().max(168).optional(),
  staggerSeconds: z17.number().int().min(0).max(3600).optional(),
  channelIds: z17.array(uuid).max(20).optional()
});
var scheduledTaskView = z17.object({
  id: scheduledTaskId,
  tenantId,
  pluginId,
  scopeType: scheduledTaskScopeType,
  scopeId: uuid,
  paramsOverrides: z17.record(z17.string()),
  scheduleSpec,
  status: scheduledTaskStatus,
  offlinePolicy,
  retryWindowHours: z17.number().int(),
  staggerSeconds: z17.number().int(),
  channelIds: z17.array(uuid),
  /** `null` = no vuelve a dispararse (pausada, o `once` ya disparada). */
  nextRunAt: z17.coerce.date().nullable(),
  createdAt: z17.coerce.date(),
  updatedAt: z17.coerce.date()
});
var scheduledTaskTriggerKind = z17.enum(["scheduled", "manual"]);
var scheduledTaskRunStatus = z17.enum(["pending", "running", "completed", "partial"]);
var scheduledTaskRunView = z17.object({
  id: scheduledTaskRunId,
  tenantId,
  scheduledTaskId,
  triggerKind: scheduledTaskTriggerKind,
  status: scheduledTaskRunStatus,
  nodesTotal: z17.number().int(),
  nodesSucceeded: z17.number().int(),
  nodesFailed: z17.number().int(),
  nodesSkippedOffline: z17.number().int(),
  nodesAwaitingReconnect: z17.number().int(),
  nodesExpired: z17.number().int(),
  startedAt: z17.coerce.date(),
  finishedAt: z17.coerce.date().nullable()
});
var runScheduledTaskNowResponse = z17.object({ runId: scheduledTaskRunId });

// src/stream-codec.ts
var MSG_EXEC_STDOUT = 1;
var MSG_EXEC_STDERR = 2;
var MSG_EXEC_RESULT = 3;
var MSG_EXEC_ERROR = 4;
var MSG_FILE_HEADER = 32;
var MSG_FILE_DATA = 33;
var MSG_FILE_TRAILER = 34;
var MSG_FILE_RESULT = 35;
var MSG_FILE_ERROR = 36;
var MSG_HEADER_SIZE = 5;
var MAX_MSG_PAYLOAD = 1 << 20;
function encodeMessage(kind, payload) {
  if (payload.length <= MAX_MSG_PAYLOAD) {
    return Buffer.concat([headerFor(kind, payload.length), payload]);
  }
  const parts = [];
  for (let offset = 0; offset < payload.length; offset += MAX_MSG_PAYLOAD) {
    const chunk = payload.subarray(offset, offset + MAX_MSG_PAYLOAD);
    parts.push(headerFor(kind, chunk.length), chunk);
  }
  return Buffer.concat(parts);
}
function encodeJsonMessage(kind, value) {
  return encodeMessage(kind, Buffer.from(JSON.stringify(value), "utf8"));
}
function headerFor(kind, length) {
  const header = Buffer.allocUnsafe(MSG_HEADER_SIZE);
  header.writeUInt8(kind, 0);
  header.writeUInt32BE(length, 1);
  return header;
}
async function* decodeMessages(source) {
  let pending = Buffer.alloc(0);
  for await (const chunk of source) {
    pending = pending.length === 0 ? Buffer.from(chunk) : Buffer.concat([pending, chunk]);
    for (; ; ) {
      if (pending.length < MSG_HEADER_SIZE) {
        break;
      }
      const length = pending.readUInt32BE(1);
      if (length > MAX_MSG_PAYLOAD) {
        throw new Error(`mensaje del t\xFAnel de ${length} bytes: pasa del m\xE1ximo`);
      }
      if (pending.length < MSG_HEADER_SIZE + length) {
        break;
      }
      const kind = pending.readUInt8(0);
      yield { kind, payload: pending.subarray(MSG_HEADER_SIZE, MSG_HEADER_SIZE + length) };
      pending = pending.subarray(MSG_HEADER_SIZE + length);
    }
  }
  if (pending.length > 0) {
    throw new Error(`el stream termin\xF3 con ${pending.length} bytes sueltos`);
  }
}
var TextAccumulator = class {
  decoder = new TextDecoder("utf-8");
  text = "";
  push(payload) {
    this.text += this.decoder.decode(payload, { stream: true });
  }
  value() {
    return this.text + this.decoder.decode();
  }
};
function parseJsonPayload(payload) {
  return JSON.parse(payload.toString("utf8"));
}

// src/users.ts
import { z as z18 } from "zod";
var operatorPassword = z18.string().min(12, "La contrase\xF1a debe tener al menos 12 caracteres").max(200);
var userStatusFilter = z18.enum(["active", "inactive", "all"]);
var userRecord = z18.object({
  id: userId,
  tenantId,
  email: z18.string().email(),
  name: z18.string(),
  isActive: z18.boolean(),
  mfaEnabled: z18.boolean(),
  /** Se puso una contraseña temporal: el usuario tiene que cambiarla al entrar. */
  mustChangePassword: z18.boolean(),
  lastLoginAt: z18.coerce.date().nullable(),
  createdAt: z18.coerce.date(),
  permissions: z18.array(permission),
  highestRole: role.nullable()
});
var listUsersQuery = z18.object({
  role: role.optional(),
  scopeType: scopeType.optional(),
  scopeId: z18.string().uuid().optional(),
  status: userStatusFilter.default("all"),
  /** Coincidencia parcial sobre correo y nombre. */
  search: z18.string().trim().min(1).max(160).optional()
});
var createUserRequest = z18.object({
  email: z18.string().email().max(254),
  name: z18.string().trim().min(1).max(160),
  password: operatorPassword.optional(),
  permissions: z18.array(permission).max(50).optional()
});
var createdUser = userRecord.extend({
  temporaryPassword: z18.string().nullable()
});
var updateUserRequest = z18.object({
  name: z18.string().trim().min(1).max(160).optional(),
  role: role.optional(),
  isActive: z18.boolean().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "No hay nada que cambiar"
});
var grantPermissionRequest = z18.object({ role });
var passwordResetResponse = z18.object({
  temporaryPassword: z18.string(),
  /** Siempre `true`: por eso es temporal. Va explícito para el panel. */
  mustChangePassword: z18.literal(true)
});
var changeOwnPasswordRequest = z18.object({
  currentPassword: z18.string().min(1),
  newPassword: operatorPassword
});
var permissionsChangedOp = z18.enum([
  "user.created",
  "user.updated",
  "user.deleted",
  "user.role_changed",
  "user.password_reset",
  "user.password_changed",
  "permission.granted",
  "permission.revoked"
]);
var permissionsChangedDetail = z18.object({
  op: permissionsChangedOp,
  targetUserId: userId,
  targetEmail: z18.string().email(),
  before: z18.object({
    isActive: z18.boolean().nullable(),
    permissions: z18.array(permission)
  }),
  after: z18.object({
    isActive: z18.boolean().nullable(),
    permissions: z18.array(permission)
  })
});
export {
  ACTIONS,
  AGENT_RELEASE_CHANNELS,
  CONFIRM_ARGUMENT,
  ENROLLMENT_CODE_STATUS_MAX_NODES,
  ENROLLMENT_LONG_MAX_EXPIRY_MINUTES,
  ENROLLMENT_MANUAL_CODE_MAX_LENGTH,
  ENROLLMENT_MAX_USES,
  EVENTS_CHANNEL_PATTERN,
  HEALTH_RESOLUTIONS,
  HEALTH_RESOLUTION_ORDER,
  HEALTH_SERIES_DEFAULT_RANGE_MS,
  HEALTH_SERIES_MAX_POINTS,
  LOGO_MAX_BYTES,
  LOGO_MAX_CHARS,
  MAX_MSG_PAYLOAD,
  METRIC_UNIT,
  MSG_EXEC_ERROR,
  MSG_EXEC_RESULT,
  MSG_EXEC_STDERR,
  MSG_EXEC_STDOUT,
  MSG_FILE_DATA,
  MSG_FILE_ERROR,
  MSG_FILE_HEADER,
  MSG_FILE_RESULT,
  MSG_FILE_TRAILER,
  MSG_HEADER_SIZE,
  NAMED_TARGET_METRICS,
  NODE_WATCH_DOWN_SECONDS,
  NODE_WATCH_METRICS,
  SCOPE_BREADTH,
  SEVERITY_RANK,
  SYSTEM_AUDIT_ACTIONS,
  TUNNEL_PROTOCOL,
  TextAccumulator,
  WATCH_METRIC,
  accessTokenClaims,
  acknowledgeAlertRequest,
  actionForToolName,
  actionNames,
  actor,
  agentArtifact,
  agentCapability,
  agentReleaseChannel,
  agentReleaseManifest,
  agentUpdateOutcome,
  agentUpdateReport,
  agentVersion,
  alertComparison,
  alertEffect,
  alertEvalState,
  alertGroupKey,
  alertMetric,
  alertNotice,
  alertRuleName,
  alertRuleSource,
  alertRuleSpec,
  alertRuleView,
  alertSeverity,
  alertStatus,
  alertView,
  asciicastEventLine,
  asciicastFile,
  asciicastHeaderLine,
  auditChannel,
  auditEntry,
  auditEvent,
  auditOutcome,
  auditPage,
  branchId,
  branchRecord,
  brandingColor,
  brandingSpec,
  brandingView,
  buildToolList,
  changeOwnPasswordRequest,
  chooseHealthResolution,
  clientId,
  clientRecord,
  containersList,
  createBranchRequest,
  createClientRequest,
  createEnrollmentTokenRequest,
  createEnrollmentTokenResponse,
  createMcpCredentialRequest,
  createMcpCredentialResponse,
  createNotificationChannelRequest,
  createScheduledTaskRequest,
  createUserRequest,
  createdUser,
  currentUser,
  decodeMessages,
  deliveryStatus,
  discordChannelConfig,
  emailChannelConfig,
  emptyBranding,
  encodeJsonMessage,
  encodeMessage,
  enrollmentCodeCommands,
  enrollmentCodeEnrolledNode,
  enrollmentCodeStatusRequest,
  enrollmentCodeStatusResponse,
  enrollmentIssue,
  enrollmentList,
  enrollmentRevoke,
  enrollmentTokenFormat,
  enrollmentTokenSummary,
  eventsChannel,
  filesDelete,
  filesDownload,
  filesList,
  filesUpload,
  grantPermissionRequest,
  hasBranding,
  healthBucketStart,
  healthConnectionStatus,
  healthResolution,
  healthSample,
  healthSeriesPoint,
  healthSeriesQuery,
  healthSeriesResponse,
  hierarchyTree,
  isActionName,
  isNamedTargetMetric,
  isNodeWatchMetric,
  issueEnrollmentTokenRequest,
  issueEnrollmentTokenResponse,
  latestAgentReleaseQuery,
  latestAgentReleaseResponse,
  listAlertsQuery,
  listAuditQuery,
  listEnrollmentTokensResponse,
  listMcpCredentialsResponse,
  listRecordingsQuery,
  listUsersQuery,
  loginRequest,
  loginResponse,
  mcpCredentialSummary,
  mfaVerifyRequest,
  nodeAlias,
  nodeId,
  nodeInventory,
  nodeRef,
  nodeStatus,
  nodeSummary,
  nodeToggleMetric,
  nodeToggleRequest,
  nodeToggleView,
  nodeType,
  nodeWatchKind,
  nodeWatchRequest,
  nodeWatchStatus,
  nodeWatchTarget,
  nodeWatchView,
  nodesDecommission,
  nodesGet,
  nodesInventory,
  nodesList,
  notificationChannelConfig,
  notificationChannelKind,
  notificationChannelSecret,
  notificationChannelView,
  notificationDeliveryView,
  notificationMessage,
  offlinePolicy,
  operatorPassword,
  osFamily,
  parseJsonPayload,
  passwordResetResponse,
  permission,
  permissionsChangedDetail,
  permissionsChangedOp,
  pluginEnablementView,
  pluginId,
  pluginParam,
  pluginParamType,
  pluginSpec,
  pluginSpecShape,
  pluginStep,
  pluginView,
  pluginsCreate,
  pluginsDelete,
  pluginsGet,
  pluginsList,
  pluginsSetEnablement,
  pluginsUpdate,
  presenceKey,
  presenceRecord,
  processesKill,
  processesList,
  recordingChunk,
  recordingFormat,
  recordingKind,
  recordingPlayback,
  recordingStatus,
  recordingView,
  refinePluginSpec,
  refreshRequest,
  resetCode,
  resolveEnrollmentCodeRequest,
  resolveEnrollmentCodeResponse,
  resolveToolCall,
  resourcePath,
  retentionPolicySpec,
  retentionPolicyView,
  revokeEnrollmentTokenRequest,
  revokedEnrollmentToken,
  role,
  runScheduledTaskNowResponse,
  scheduleSpec,
  scheduledTaskId,
  scheduledTaskRunId,
  scheduledTaskRunStatus,
  scheduledTaskRunView,
  scheduledTaskScopeType,
  scheduledTaskStatus,
  scheduledTaskTriggerKind,
  scheduledTaskView,
  scopeType,
  screenOpen,
  scriptRun,
  servicesControl,
  servicesList,
  sessionId,
  sessionTokens,
  sessionsListActive,
  setPluginEnablementRequest,
  slackChannelConfig,
  streamKind,
  tenantId,
  terminalOpen,
  toolNameFor,
  tunnelEvent,
  updateAlertRuleRequest,
  updateBranchRequest,
  updateClientRequest,
  updateNodeRequest,
  updateNotificationChannelRequest,
  updatePluginRequest,
  updateScheduledTaskRequest,
  updateUserRequest,
  userId,
  userRecord,
  userStatusFilter,
  uuid,
  watchesList,
  watchesSet,
  whatsappChannelConfig
};
//# sourceMappingURL=index.js.map